import { useReservationModalTranslation } from "../reservations/reservationModalI18n";
import React, { useRef } from "react";
import axios from "../api/axiosInstance";
import { useCurrencyDisplay } from "../currency/CurrencyDisplayContext";
import {
  currencyAmountStep,
  formatCurrencyAmount,
  getCurrencyMinorUnits,
  hasValidCurrencyPrecision,
  quantizeCurrencyAmount,
} from "../currency/currencyPrecision";

// ─── Style constants ────────────────────────────────────────────────────────

const overlayStyle = {
  position: "fixed",
  inset: 0,
  background: "rgba(0, 0, 0, 0.85)",
  zIndex: 22000,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "8px 12px",
  overflowY: "auto",
  boxSizing: "border-box",
};

const shellStyle = {
  width: "100%",
  maxWidth: "620px",
  borderRadius: "22px",
  padding: "28px",
  background:
    "linear-gradient(180deg, rgba(10, 10, 18, 0.95), rgba(8, 13, 25, 0.95)) padding-box, " +
    "linear-gradient(170deg, rgba(14, 165, 233, 0.35), rgba(59, 130, 246, 0.25)) border-box",
  border: "1px solid rgba(148, 163, 184, 0.25)",
  boxShadow: "0 30px 60px rgba(2, 6, 23, 0.65)",
  display: "flex",
  flexDirection: "column",
  maxHeight: "calc(100dvh - 16px)",
  boxSizing: "border-box",
  margin: "auto 0",
  overflow: "hidden",
};

const contentWrapperStyle = {
  flex: "1 1 auto",
  minHeight: 0,
  overflowY: "auto",
  paddingRight: "2px",
  maxHeight: "none",
};

const sectionStyle = {
  marginTop: "18px",
  borderRadius: "16px",
  padding: "18px",
  background: "rgba(15, 23, 42, 0.85)",
  border: "1px solid rgba(148, 163, 184, 0.25)",
};

const rowCardStyle = {
  marginBottom: "14px",
  borderRadius: "14px",
  padding: "16px",
  background: "rgba(10, 14, 30, 0.9)",
  border: "1px solid rgba(148, 163, 184, 0.2)",
};

const sectionTitleStyle = {
  margin: 0,
  fontSize: "1.05rem",
  fontWeight: 800,
  color: "#f8fafc",
};

const sectionTextStyle = {
  margin: "4px 0 0",
  fontSize: "0.78rem",
  color: "rgba(226, 232, 240, 0.6)",
};

const labelStyle = {
  fontSize: "0.85rem",
  fontWeight: 600,
  color: "#e2e8f0",
  marginBottom: "6px",
  display: "block",
};

const inputFieldStyle = {
  width: "100%",
  borderRadius: "10px",
  padding: "10px 12px",
  background: "rgba(15, 23, 42, 0.9)",
  border: "1px solid rgba(148, 163, 184, 0.45)",
  color: "#e2e8f0",
  fontSize: "0.95rem",
  boxSizing: "border-box",
};

const inputDisabledStyle = {
  ...inputFieldStyle,
  opacity: 0.45,
  cursor: "not-allowed",
};

const amountInputWrapperStyle = {
  position: "relative",
};

const amountSuffixStyle = {
  position: "absolute",
  right: "12px",
  top: "50%",
  transform: "translateY(-50%)",
  color: "rgba(226, 232, 240, 0.78)",
  fontWeight: 800,
  pointerEvents: "none",
};

const actionsRowStyle = {
  marginTop: "22px",
  display: "flex",
  gap: "12px",
  flexWrap: "wrap",
};

const primaryButtonStyle = {
  borderRadius: "12px",
  padding: "12px 26px",
  fontWeight: 700,
  letterSpacing: "0.03em",
  cursor: "pointer",
  flex: 1,
  border: "1px solid rgba(56, 189, 248, 0.8)",
  background:
    "linear-gradient(135deg, rgba(14, 165, 233, 0.8), rgba(59, 130, 246, 0.9))",
  color: "#eff6ff",
};

const secondaryButtonStyle = {
  borderRadius: "12px",
  padding: "12px 26px",
  fontWeight: 700,
  letterSpacing: "0.03em",
  cursor: "pointer",
  flex: 1,
  border: "1px solid rgba(148, 163, 184, 0.5)",
  background: "transparent",
  color: "#e2e8f0",
};

const ghostButtonStyle = {
  borderRadius: "10px",
  padding: "7px 16px",
  fontWeight: 600,
  fontSize: "0.85rem",
  cursor: "pointer",
  border: "1px solid rgba(148, 163, 184, 0.35)",
  background: "transparent",
  color: "#cbd5e1",
};

const dangerGhostButtonStyle = {
  ...ghostButtonStyle,
  border: "1px solid rgba(239, 68, 68, 0.5)",
  color: "#fca5a5",
};

const errorBannerStyle = {
  marginTop: "14px",
  borderRadius: "10px",
  padding: "10px 14px",
  background: "rgba(239, 68, 68, 0.15)",
  border: "1px solid rgba(239, 68, 68, 0.4)",
  color: "#fca5a5",
  fontSize: "0.88rem",
};

const toastOverlayStyle = {
  position: "fixed",
  top: "14px",
  left: 0,
  right: 0,
  display: "flex",
  justifyContent: "center",
  pointerEvents: "none",
  zIndex: 25000,
};

const toastCardStyle = {
  background:
    "linear-gradient(180deg, rgba(14, 165, 233, 0.9), rgba(59, 130, 246, 0.8)) padding-box, " +
    "linear-gradient(170deg, rgba(14, 165, 233, 0.45), rgba(59, 130, 246, 0.35)) border-box",
  border: "1px solid rgba(148, 163, 184, 0.4)",
  borderRadius: "18px",
  padding: "14px 24px",
  color: "#eff6ff",
  fontWeight: 700,
  boxShadow: "0 20px 40px rgba(2, 6, 23, 0.6)",
  maxWidth: "420px",
  width: "min(90vw, 420px)",
  textAlign: "center",
};

const savingOverlayStyle = {
  position: "fixed",
  inset: 0,
  backgroundColor: "rgba(0, 0, 0, 0.75)",
  color: "white",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "1.5rem",
  fontWeight: "bold",
  zIndex: 23000,
  pointerEvents: "none",
};

// ─── Chip styles ─────────────────────────────────────────────────────────────

const chipGroupStyle = {
  display: "flex",
  flexWrap: "wrap",
  gap: "8px",
  marginTop: "10px",
};

const chipInactive = {
  borderRadius: "20px",
  padding: "7px 16px",
  fontWeight: 600,
  fontSize: "0.85rem",
  cursor: "pointer",
  border: "1px solid rgba(148, 163, 184, 0.35)",
  background: "rgba(15, 23, 42, 0.6)",
  color: "#94a3b8",
};

const chipActive = {
  ...chipInactive,
  border: "1px solid rgba(56, 189, 248, 0.75)",
  background: "linear-gradient(135deg, rgba(14, 165, 233, 0.2), rgba(59, 130, 246, 0.3))",
  color: "#7dd3fc",
};

const chipDisabled = {
  ...chipInactive,
  opacity: 0.35,
  cursor: "not-allowed",
};

// ─── Mode chips ───────────────────────────────────────────────────────────────

const modeChipInactive = {
  ...chipInactive,
  padding: "9px 18px",
  fontSize: "0.875rem",
};

const modeChipActive = {
  ...chipActive,
  padding: "9px 18px",
  fontSize: "0.875rem",
  fontWeight: 700,
};

const getAmountInputStyle = ({ disabled, isPercentage }) => ({
  ...(disabled ? inputDisabledStyle : inputFieldStyle),
  paddingRight: isPercentage ? "36px" : inputFieldStyle.padding,
});

// ─── Data ────────────────────────────────────────────────────────────────────

const allOverrideTypes = [
  { value: "absolute", label: "Absolute" },
  { value: "percentage", label: "Percentage" },
  { value: "free_night", label: "Complimentary" },
];

// Types available per mode
const typesByMode = {
  all: ["absolute", "percentage"],
  total: ["absolute", "free_night"],
  nights: ["absolute", "percentage", "free_night"],
};

const operations = [
  { value: "set", label: "Set" },
  { value: "increase", label: "Increase" },
  { value: "decrease", label: "Decrease" },
];

const operationsForType = (overrideType) =>
  overrideType === "percentage"
    ? operations.filter((operation) => operation.value !== "set")
    : operations;

const modes = [
  { value: "all", label: "Affect all nightly rates" },
  { value: "total", label: "Set total price" },
  { value: "nights", label: "Specific nights only" },
];

// ─── Helper: Chip ─────────────────────────────────────────────────────────────

function Chip({ label, selected, onClick, disabled }) {
  const style = disabled ? chipDisabled : selected ? chipActive : chipInactive;
  return (
    <button
      type="button"
      className={`rgv-linkedChoice${selected ? " is-selected" : ""}`}
      style={style}
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
    >
      {label}
    </button>
  );
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function OverridePopup({
  reservationId,
  isOpen,
  onClose,
  onApplied,
  baseCurrency = "EUR",
  nightlyPrices: suppliedNightlyPrices = null,
  onApplyOverrides = null,
  previousCategoryPrice = null,
}) {
  const { t: tr, errorText } = useReservationModalTranslation();
  const { currencyCatalog = [] } = useCurrencyDisplay();
  const [mode, setMode] = React.useState("all");
  const [nightlyPrices, setNightlyPrices] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const [globalOverride, setGlobalOverride] = React.useState({
    override_type: "",
    operation: "",
    override_amount: "",
    reason: "",
  });
  const [rows, setRows] = React.useState([]);
  const [submitting, setSubmitting] = React.useState(false);
  const [toastMessage, setToastMessage] = React.useState("");
  const toastTimer = useRef(null);

  React.useEffect(() => {
    if (!isOpen) return;
    setLoading(true);
    setSubmitting(false);
    setError("");
    setToastMessage("");
    if (Array.isArray(suppliedNightlyPrices)) {
      setNightlyPrices(suppliedNightlyPrices);
      setMode("all");
      setGlobalOverride({ override_type: "", operation: "", override_amount: "", reason: "" });
      setRows([]);
      setLoading(false);
      return;
    }
    if (!reservationId) {
      setNightlyPrices([]);
      setError("No nightly prices are available for this override.");
      setLoading(false);
      return;
    }
    axios
      .get(`/api/reservation-drafts/${reservationId}/nightly-prices`)
      .then((res) => {
        setNightlyPrices(res.data || []);
        setMode("all");
        setGlobalOverride({ override_type: "", operation: "", override_amount: "", reason: "" });
        setRows([]);
      })
      .catch((err) => {
        setError(err?.response?.data?.error || "Error loading nightly prices for reservation.");
      })
      .finally(() => setLoading(false));
    // Reload only when the popup is opened for another reservation/preview.
    // Parent price-review refreshes must not reset the form or its success toast.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, reservationId]);

  React.useEffect(() => {
    return () => {
      if (toastTimer.current) clearTimeout(toastTimer.current);
    };
  }, []);

  if (!isOpen) return null;

  const availableDates = nightlyPrices.filter(
    (n) => !rows.some((r) => String(r.nightly_price_id) === String(n.id))
  );
  const parsedPreviousCategoryPrice = Number(previousCategoryPrice);
  const hasPreviousCategoryPrice =
    previousCategoryPrice !== null &&
    previousCategoryPrice !== undefined &&
    previousCategoryPrice !== "" &&
    Number.isFinite(parsedPreviousCategoryPrice) &&
    parsedPreviousCategoryPrice >= 0;

  const showToast = (message) => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToastMessage(message);
    toastTimer.current = setTimeout(() => {
      setToastMessage("");
      onClose && onClose();
    }, 2000);
  };

  const handleClose = () => {
    setRows([]);
    setGlobalOverride({ override_type: "", operation: "", override_amount: "", reason: "" });
    onClose();
  };

  const handleModeChange = (val) => {
    setMode(val);
    // Reset type if not valid for the new mode
    const allowed = typesByMode[val];
    if (globalOverride.override_type && !allowed.includes(globalOverride.override_type)) {
      setGlobalOverride((prev) => ({ ...prev, override_type: "", operation: "", override_amount: "" }));
    } else if (globalOverride.override_type === "percentage" && globalOverride.operation === "set") {
      setGlobalOverride((prev) => ({ ...prev, operation: "" }));
    }
  };

  const persistOverrides = async (
    payload,
    successMessage = "Overrides applied successfully."
  ) => {
    setError("");
    setSubmitting(true);
    try {
      const res = onApplyOverrides
        ? await onApplyOverrides(payload)
        : await axios.post(
            `/api/reservation-drafts/${reservationId}/overrides`,
            payload,
            { timeout: 45000 }
          );
      if (onApplied) {
        try {
          await Promise.race([
            Promise.resolve(onApplied(res?.data ?? res)),
            new Promise((resolve) => setTimeout(resolve, 8000)),
          ]);
        } catch (callbackErr) {
          console.warn("[OverridePopup][APPLY_REFRESH][WARN]", callbackErr);
        }
      }
      setSubmitting(false);
      showToast(successMessage);
    } catch (err) {
      console.error("[OverridePopup][APPLY][ERR]", err);
      setError(
        err?.response?.data?.error ||
          err?.message ||
          "Error applying overrides."
      );
      setSubmitting(false);
    }
  };

  const handleKeepPreviousCategoryPrice = async () => {
    if (!hasPreviousCategoryPrice) {
      setError("The previous category price is not available.");
      return;
    }
    const previousPrice = quantizeCurrencyAmount(
      parsedPreviousCategoryPrice,
      baseCurrency,
      currencyCatalog
    );
    await persistOverrides(
      {
        overrides: [
          {
            scope: "total_reservation",
            override_type: "absolute",
            operation: "set",
            override_amount: previousPrice,
            reason: "Previous category price retained",
          },
        ],
      },
      "Previous category price retained."
    );
  };

  const handleApply = async () => {
    setError("");
    let payload = { overrides: [] };

    if (mode === "all") {
        if (
          !globalOverride.override_type ||
          (globalOverride.override_type !== "free_night" &&
            (!globalOverride.operation ||
              (globalOverride.operation === "set" && globalOverride.override_type === "percentage") ||
              globalOverride.override_amount === ""))
        ) {
          setError("Please complete all required fields.");
          return;
        }
        payload.overrides = nightlyPrices.map((n) => ({
          nightly_price_id: n.id,
          override_type: globalOverride.override_type,
          operation: globalOverride.override_type === "free_night" ? null : globalOverride.operation,
          override_amount:
            globalOverride.override_type === "free_night" ? 0 : Number(globalOverride.override_amount),
          reason: globalOverride.reason || null,
        }));
    } else if (mode === "total") {
        if (globalOverride.override_type === "free_night") {
          payload.overrides = [
            {
              scope: "total_reservation",
              override_type: "free_night",
              operation: null,
              override_amount: 0,
              reason: globalOverride.reason || null,
            },
          ];
        } else {
          if (!globalOverride.override_amount) {
            setError("Please enter a total amount.");
            return;
          }
          payload.overrides = [
            {
              scope: "total_reservation",
              override_type: globalOverride.override_type,
              operation: "set",
              override_amount: Number(globalOverride.override_amount),
              reason: globalOverride.reason || null,
            },
          ];
        }
    } else {
        if (
          rows.some(
            (r) =>
              !r.nightly_price_id ||
              !r.override_type ||
              (r.override_type !== "free_night" &&
                (!r.operation ||
                  (r.operation === "set" && r.override_type === "percentage") ||
                  r.override_amount === ""))
          )
        ) {
          setError("Please complete all required fields in each row.");
          return;
        }
        payload.overrides = rows.map((r) => ({
          nightly_price_id: Number(r.nightly_price_id),
          override_type: r.override_type,
          operation: r.override_type === "free_night" ? null : r.operation,
          override_amount: r.override_type === "free_night" ? null : Number(r.override_amount),
          reason: r.reason || null,
        }));
    }

    const invalidCurrencyOverride = payload.overrides.find(
      (override) =>
        override.override_type === "absolute" &&
        !hasValidCurrencyPrecision(
          override.override_amount,
          baseCurrency,
          currencyCatalog
        )
    );
    if (invalidCurrencyOverride) {
      const minorUnits = getCurrencyMinorUnits(baseCurrency, currencyCatalog);
      setError(
        minorUnits === 0
          ? tr("{currency} amounts accept whole units only.", { currency: baseCurrency })
          : tr("{currency} amounts accept up to {decimals} decimal places.", {
              currency: baseCurrency,
              decimals: minorUnits,
            })
      );
      return;
    }

    await persistOverrides(payload);
  };

  // Render type + operation chips + amount/reason inputs for global (all / total) modes
  const renderGlobalFields = (reasonPlaceholder, allowedTypeValues, showOperation = true) => {
    const isFreeNight = globalOverride.override_type === "free_night";
    const disabled = loading;
    const availableTypes = allOverrideTypes.filter((t) => allowedTypeValues.includes(t.value));

    return (
      <div style={{ marginTop: "14px" }}>
        {/* Type chips */}
        <label style={labelStyle}>{tr("Type")}</label>
        <div style={chipGroupStyle}>
          {availableTypes.map((t) => (
            <Chip
              key={t.value}
              label={tr(t.label)}
              selected={globalOverride.override_type === t.value}
              disabled={disabled}
              onClick={() => {
                const val = t.value;
                setGlobalOverride((prev) => ({
                  ...prev,
                  override_type: val,
                  operation: val === "free_night" || (val === "percentage" && prev.operation === "set") ? "" : prev.operation,
                  override_amount: val === "free_night" ? "" : prev.override_amount,
                }));
              }}
            />
          ))}
        </div>

        {/* Operation chips — only when showOperation=true and type is not complimentary */}
        {showOperation && !isFreeNight && globalOverride.override_type && (
          <div style={{ marginTop: "14px" }}>
            <label style={labelStyle}>{tr("Operation")}</label>
            <div style={chipGroupStyle}>
              {operationsForType(globalOverride.override_type).map((o) => (
                <Chip
                  key={o.value}
                  label={tr(o.label)}
                  selected={globalOverride.operation === o.value}
                  disabled={disabled}
                  onClick={() => setGlobalOverride((prev) => ({ ...prev, operation: o.value }))}
                />
              ))}
            </div>
          </div>
        )}

        {/* Amount + Reason inputs */}
        {!isFreeNight && globalOverride.override_type && (
          <div style={{ marginTop: "14px" }}>
            <label style={labelStyle}> {tr("Amount")} {globalOverride.override_type === "percentage" ? "(%)" : tr("(base {currency})", { currency: baseCurrency })}
            </label>
            <div style={amountInputWrapperStyle}>
              <input
                type="number"
                value={globalOverride.override_amount}
                onChange={(e) =>
                  setGlobalOverride((prev) => ({ ...prev, override_amount: e.target.value }))
                }
                step={globalOverride.override_type === "percentage" ? "0.01" : currencyAmountStep(baseCurrency, currencyCatalog)}
                inputMode={globalOverride.override_type !== "percentage" && getCurrencyMinorUnits(baseCurrency, currencyCatalog) === 0 ? "numeric" : "decimal"}
                placeholder={globalOverride.override_type === "percentage" ? tr("e.g. 10") : getCurrencyMinorUnits(baseCurrency, currencyCatalog) === 0 ? tr("e.g. 100") : tr("e.g. 100.00")}
                style={getAmountInputStyle({
                  disabled,
                  isPercentage: globalOverride.override_type === "percentage",
                })}
                disabled={disabled}
              />
              {globalOverride.override_type === "percentage" && (
                <span style={amountSuffixStyle}>%</span>
              )}
            </div>
          </div>
        )}

        <div style={{ marginTop: "14px" }}>
          <label style={labelStyle}>{tr("Reason")}</label>
          <input
            type="text"
            value={globalOverride.reason}
            onChange={(e) =>
              setGlobalOverride((prev) => ({ ...prev, reason: e.target.value }))
            }
            placeholder={reasonPlaceholder}
            style={disabled ? inputDisabledStyle : inputFieldStyle}
            disabled={disabled}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="rgv-linkedOverlay rgv-overrideOverlay" style={overlayStyle}>
      <div className="rgv-linkedModal rgv-overrideModal" style={shellStyle}>
        {/* Header */}
        <div className="rgv-overrideHeader" style={{ textAlign: "center", marginBottom: "18px" }}>
          <p
            style={{
              letterSpacing: "0.35em",
              fontSize: "0.8rem",
              color: "rgba(248, 250, 252, 0.65)",
              marginBottom: "6px",
              textTransform: "uppercase",
            }}
          > {tr("Reservation pricing")} </p>
          <h2 style={{ margin: 0, fontSize: "2rem", color: "#f8fafc" }}>{tr("Apply Overrides")}</h2>
          <p style={{ marginTop: "6px", color: "rgba(226, 232, 240, 0.75)" }}> {tr("Adjust nightly rates or the total price for this reservation.")} </p>
        </div>

        <div className="rgv-overrideContent" style={contentWrapperStyle}>
          {/* Mode chips */}
          <section className="rgv-linkedSection" style={sectionStyle}>
            <h3 style={sectionTitleStyle}>{tr("Override Mode")}</h3>
            <p style={sectionTextStyle}>{tr("Choose how the override will be applied.")}</p>
            <div style={{ ...chipGroupStyle, marginTop: "14px" }}>
              {modes.map((m) => (
                <button
                  key={m.value}
                  type="button"
                  style={
                    loading
                      ? { ...modeChipInactive, opacity: 0.35, cursor: "not-allowed" }
                      : mode === m.value
                      ? modeChipActive
                      : modeChipInactive
                  }
                  onClick={() => !loading && handleModeChange(m.value)}
                  disabled={loading}
                >
                  {tr(m.label)}
                </button>
              ))}
            </div>
          </section>

          {hasPreviousCategoryPrice && (
              <section className="rgv-linkedSection" style={sectionStyle}>
                <h3 style={sectionTitleStyle}>{tr("Keep the agreed price")}</h3>
                <p style={sectionTextStyle}> {tr("Retain the previous category price of")}{" "}
                  {formatCurrencyAmount(parsedPreviousCategoryPrice, baseCurrency, { catalog: currencyCatalog })}{tr(". This can be used for a complimentary upgrade or a downgrade without a refund.")} </p>
                <button
                  type="button"
                  style={
                    submitting || loading
                      ? {
                          ...ghostButtonStyle,
                          opacity: 0.45,
                          cursor: "not-allowed",
                        }
                      : ghostButtonStyle
                  }
                  onClick={handleKeepPreviousCategoryPrice}
                  disabled={submitting || loading}
                > {tr("Keep previous category price")} </button>
              </section>
            )}

          {/* Error */}
          {error && <div style={errorBannerStyle}>{errorText(error, "Error applying overrides.")}</div>}

          {/* All nights mode */}
          {mode === "all" && (
            <section className="rgv-linkedSection" style={sectionStyle}>
              <h3 style={sectionTitleStyle}>{tr("All Nights")}</h3>
              <p style={sectionTextStyle}> {tr("Same override applied to every nightly rate in this reservation.")} </p>
              {renderGlobalFields(
                tr("Optional note (e.g. High season, manual change…)"),
                typesByMode.all
              )}
            </section>
          )}

          {/* Total reservation mode */}
          {mode === "total" && (
            <section className="rgv-linkedSection" style={sectionStyle}>
              <h3 style={sectionTitleStyle}>{tr("Total Reservation")}</h3>
              <p style={sectionTextStyle}> {tr("Override distributed across all nights to match the target total.")} </p>
              {renderGlobalFields(
                tr("Optional note (e.g. Set agreed total, group discount…)"),
                typesByMode.total,
                false
              )}
            </section>
          )}

          {/* Specific nights mode */}
          {mode === "nights" && (
            <section className="rgv-linkedSection" style={sectionStyle}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "14px" }}>
                <div>
                  <h3 style={sectionTitleStyle}>{tr("Specific Nights")}</h3>
                  <p style={sectionTextStyle}> {tr("Configure an override for each individual night.")} </p>
                </div>
                <button
                  type="button"
                  style={availableDates.length === 0 || loading ? { ...ghostButtonStyle, opacity: 0.45, cursor: "not-allowed" } : ghostButtonStyle}
                  onClick={() => {
                    setRows((prev) => [
                      ...prev,
                      { nightly_price_id: "", override_type: "", operation: "", override_amount: "", reason: "" },
                    ]);
                  }}
                  disabled={availableDates.length === 0 || loading}
                > {tr("+ Add night")} </button>
              </div>

              {rows.length === 0 && (
                <p style={{ ...sectionTextStyle, textAlign: "center", padding: "16px 0" }}> {tr("No nights added yet. Click \"Add night\" to start.")} </p>
              )}

              {rows.map((row, idx) => {
                const isComplimentary = row.override_type === "free_night";
                const rowDisabled = loading;
                return (
                  <div key={idx} className="rgv-overrideNightRow" style={rowCardStyle}>
                    {/* Date select */}
                    <div style={{ marginBottom: "12px" }}>
                      <label style={labelStyle}>{tr("Date")}</label>
                      <select
                        value={row.nightly_price_id || ""}
                        onChange={(e) => {
                          const val = e.target.value;
                          setRows((prev) => {
                            const copy = [...prev];
                            copy[idx] = { ...copy[idx], nightly_price_id: val };
                            return copy;
                          });
                        }}
                        style={rowDisabled ? inputDisabledStyle : inputFieldStyle}
                        disabled={rowDisabled}
                      >
                        <option value="">{tr("— Select date —")}</option>
                        {nightlyPrices.map((n) => {
                          const taken = rows.some(
                            (r, i) => String(r.nightly_price_id) === String(n.id) && i !== idx
                          );
                          if (taken) return null;
                          return (
                            <option key={n.id} value={n.id}>
                              {n.date} ({formatCurrencyAmount(n.price, baseCurrency, { catalog: currencyCatalog })})
                            </option>
                          );
                        })}
                      </select>
                    </div>

                    {/* Type chips */}
                    {row.nightly_price_id && (
                      <div style={{ marginBottom: "12px" }}>
                        <label style={labelStyle}>{tr("Type")}</label>
                        <div style={chipGroupStyle}>
                          {allOverrideTypes.map((t) => (
                            <Chip
                              key={t.value}
                              label={tr(t.label)}
                              selected={row.override_type === t.value}
                              disabled={rowDisabled}
                              onClick={() => {
                                const val = t.value;
                                setRows((prev) => {
                                  const copy = [...prev];
                                  copy[idx] = {
                                    ...copy[idx],
                                    override_type: val,
                                    operation:
                                      val === "free_night" ||
                                      (val === "percentage" && copy[idx].operation === "set")
                                        ? ""
                                        : copy[idx].operation,
                                    override_amount: val === "free_night" ? "" : copy[idx].override_amount,
                                  };
                                  return copy;
                                });
                              }}
                            />
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Operation chips — hidden for complimentary */}
                    {row.nightly_price_id && row.override_type && !isComplimentary && (
                      <div style={{ marginBottom: "12px" }}>
                        <label style={labelStyle}>{tr("Operation")}</label>
                        <div style={chipGroupStyle}>
                          {operationsForType(row.override_type).map((o) => (
                            <Chip
                              key={o.value}
                              label={tr(o.label)}
                              selected={row.operation === o.value}
                              disabled={rowDisabled}
                              onClick={() => {
                                setRows((prev) => {
                                  const copy = [...prev];
                                  copy[idx] = { ...copy[idx], operation: o.value };
                                  return copy;
                                });
                              }}
                            />
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Amount */}
                    {row.nightly_price_id && row.override_type && !isComplimentary && (
                      <div style={{ marginBottom: "12px" }}>
                        <label style={labelStyle}> {tr("Amount")} {row.override_type === "percentage" ? "(%)" : tr("(base {currency})", { currency: baseCurrency })}
                        </label>
                        <div style={amountInputWrapperStyle}>
                          <input
                            type="number"
                            value={row.override_amount}
                            onChange={(e) => {
                              const val = e.target.value;
                              setRows((prev) => {
                                const copy = [...prev];
                                copy[idx] = { ...copy[idx], override_amount: val };
                                return copy;
                              });
                            }}
                            step={row.override_type === "percentage" ? "0.01" : currencyAmountStep(baseCurrency, currencyCatalog)}
                            inputMode={row.override_type !== "percentage" && getCurrencyMinorUnits(baseCurrency, currencyCatalog) === 0 ? "numeric" : "decimal"}
                            placeholder={row.override_type === "percentage" ? tr("e.g. 10") : getCurrencyMinorUnits(baseCurrency, currencyCatalog) === 0 ? tr("e.g. 100") : tr("e.g. 100.00")}
                            style={getAmountInputStyle({
                              disabled: rowDisabled,
                              isPercentage: row.override_type === "percentage",
                            })}
                            disabled={rowDisabled}
                          />
                          {row.override_type === "percentage" && (
                            <span style={amountSuffixStyle}>%</span>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Reason */}
                    {row.nightly_price_id && row.override_type && (
                      <div>
                        <label style={labelStyle}>{tr("Reason")}</label>
                        <input
                          type="text"
                          value={row.reason}
                          onChange={(e) => {
                            const val = e.target.value;
                            setRows((prev) => {
                              const copy = [...prev];
                              copy[idx] = { ...copy[idx], reason: val };
                              return copy;
                            });
                          }}
                          placeholder={tr("Optional note (e.g. High season, manual change…)")}
                          style={rowDisabled ? inputDisabledStyle : inputFieldStyle}
                          disabled={rowDisabled}
                        />
                      </div>
                    )}

                    {rows.length > 1 && (
                      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "10px" }}>
                        <button
                          type="button"
                          style={rowDisabled ? { ...dangerGhostButtonStyle, opacity: 0.45, cursor: "not-allowed" } : dangerGhostButtonStyle}
                          onClick={() => setRows((prev) => prev.filter((_, i) => i !== idx))}
                          disabled={rowDisabled}
                        > {tr("Remove")} </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </section>
          )}

          {/* Actions */}
          <div className="rgv-linkedActions" style={actionsRowStyle}>
            <button
              type="button"
              style={submitting || loading ? { ...primaryButtonStyle, opacity: 0.6, cursor: "not-allowed" } : primaryButtonStyle}
              onClick={handleApply}
              disabled={submitting || loading}
            >
              {submitting ? tr("Applying…") : tr("Apply")}
            </button>
            <button
              type="button"
              style={submitting ? { ...secondaryButtonStyle, opacity: 0.6, cursor: "not-allowed" } : secondaryButtonStyle}
              onClick={handleClose}
              disabled={submitting}
            > {tr("Cancel")} </button>
          </div>
        </div>
      </div>

      {submitting && <div className="rgv-linkedSaving" style={savingOverlayStyle}>{tr("Applying overrides…")}</div>}

      {toastMessage && (
        <div className="rgv-linkedToastOverlay" style={toastOverlayStyle}>
          <div className="rgv-linkedToast" style={toastCardStyle}>{tr(toastMessage)}</div>
        </div>
      )}
    </div>
  );
}
